import { computed, reactive } from "vue";
import { listEnabledGraphs } from "@/api/graphApi";

const STORAGE_KEY = "admin-selectedGraph";

const state = reactive({
  graphId: null,
  graphOptions: [],
  graphLoading: false,
});

let loadPromise = null;

function parseStoredGraphId() {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw == null || raw === "") {
    return null;
  }
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

function persistGraphId(id) {
  if (id == null) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, String(id));
}

function resolveInitialGraphId(options) {
  if (!options.length) {
    return null;
  }
  const stored = parseStoredGraphId();
  if (stored != null && options.some((o) => o.value === stored)) {
    return stored;
  }
  return options[0].value;
}

async function loadGraphOptions() {
  state.graphLoading = true;
  try {
    const res = await listEnabledGraphs();
    if (res?.code === 0 && Array.isArray(res.data)) {
      state.graphOptions = res.data.map((g) => ({
        value: g.id,
        label: g.graphName,
      }));
      const resolved = resolveInitialGraphId(state.graphOptions);
      if (state.graphId == null) {
        state.graphId = resolved;
        if (resolved != null) {
          persistGraphId(resolved);
        }
      } else if (
        state.graphId != null &&
        !state.graphOptions.some((o) => o.value === state.graphId)
      ) {
        state.graphId = resolved;
        persistGraphId(resolved);
      }
    } else {
      state.graphOptions = [];
      state.graphId = null;
      persistGraphId(null);
    }
  } finally {
    state.graphLoading = false;
  }
}

export function ensureGraphOptionsLoaded() {
  if (!loadPromise) {
    loadPromise = loadGraphOptions();
  }
  return loadPromise;
}

export function setGraphId(id) {
  if (id == null) {
    state.graphId = null;
    persistGraphId(null);
    return;
  }
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) {
    return;
  }
  state.graphId = numericId;
  persistGraphId(numericId);
}

export function useAdminSelectedGraph() {
  const graphId = computed({
    get: () => state.graphId,
    set: (value) => setGraphId(value),
  });

  return {
    graphId,
    graphOptions: computed(() => state.graphOptions),
    graphLoading: computed(() => state.graphLoading),
    ensureGraphOptionsLoaded,
    setGraphId,
  };
}
